import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { FlightDetail, FlightHistoryRow } from '@/types/flight';
import { fetchFlight, fetchFlightHistory } from './api';

type ErrorKey = 'schedule.detail.errorForbidden' | 'schedule.detail.errorLoad';

/** What one completed load produced, tagged with the id it answered for. */
interface Outcome {
  id: number;
  flight: FlightDetail | null;
  /** Newest first — the reverse of what the endpoint returns. */
  history: FlightHistoryRow[];
  /** The flight loaded but its history did not; the panel still has something to show. */
  historyFailed: boolean;
  errorKey: ErrorKey | null;
}

interface DetailState {
  flight: FlightDetail | null;
  history: FlightHistoryRow[];
  historyFailed: boolean;
  isLoading: boolean;
  /** True when the id resolved to `200 null` — the row is gone or never existed. */
  notFound: boolean;
  errorKey: ErrorKey | null;
}

/**
 * Loads one flight and its history.
 *
 * The two requests go together but fail apart: a flight whose history cannot be
 * fetched is still worth showing, so a failed history is reported as a note inside
 * the panel rather than replacing the whole thing with an error. A failed FLIGHT is
 * the panel's own failure.
 *
 * Loading is derived from the tag, as in useSchedule: state is only ever set from a
 * settled promise, never synchronously in the effect body.
 */
export const useFlightDetail = (id: number | null): DetailState => {
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    if (id === null) return undefined;

    const controller = new AbortController();

    // `allSettled`, not `all`: the history is allowed to fail on its own.
    Promise.allSettled([
      fetchFlight(id, controller.signal),
      fetchFlightHistory(id, controller.signal),
    ])
      .then(([flightResult, historyResult]) => {
        if (controller.signal.aborted) return;

        if (flightResult.status === 'rejected') {
          const forbidden =
            flightResult.reason instanceof ApiError && flightResult.reason.isForbidden;

          setOutcome({
            id,
            flight: null,
            history: [],
            historyFailed: true,
            errorKey: forbidden ? 'schedule.detail.errorForbidden' : 'schedule.detail.errorLoad',
          });
          return;
        }

        setOutcome({
          id,
          flight: flightResult.value,
          // The endpoint gives oldest first; a panel is read newest first, because the
          // last thing that happened is the thing being asked about.
          history:
            historyResult.status === 'fulfilled' ? [...historyResult.value].reverse() : [],
          historyFailed: historyResult.status === 'rejected',
          errorKey: null,
        });
      })
      .catch(() => {
        // allSettled does not reject; this is belt and braces.
      });

    return () => controller.abort();
  }, [id]);

  const isCurrent = outcome?.id === id;

  return {
    flight: isCurrent ? outcome.flight : null,
    history: isCurrent ? outcome.history : [],
    historyFailed: isCurrent ? outcome.historyFailed : false,
    isLoading: id !== null && !isCurrent,
    // A 200 with a null body is the API's way of saying "no such id" — distinct
    // from a request that failed, and it deserves its own message.
    notFound: isCurrent && outcome.flight === null && outcome.errorKey === null,
    errorKey: isCurrent ? outcome.errorKey : null,
  };
};
